import { Markdown } from '@terminal/components/markdown/Markdown';
import clsx from 'clsx';
import { memo } from 'react';
import { Components, Options } from 'react-markdown';
import styles from './ResearchMarkdown.module.scss';

interface ResearchMarkdownProps {
  message: string;
  className?: string;
  components?: Partial<Components>;
  remarkPlugins?: Options['remarkPlugins'];
}

const CITATION_TITLE = String.raw`"(?:citation|provenance)"`;
const CITATION_LINK = String.raw`\[[^\]\n]+\]\([^)\n]+?\s+${CITATION_TITLE}\)`;
const TRAILING_CITATION_PUNCTUATION = new RegExp(
  String.raw`\[([^\]\n]+)\]\(([^)\n]+?\s+${CITATION_TITLE})\)[ \t]*(?:\r?\n[ \t]*)?([.,;:?!])`,
  'g'
);
const LIST_MARKER_BEFORE_CITATION = new RegExp(
  String.raw`(^[ \t]*(?:\d+\.|[-+*]))[ \t]*\r?\n[ \t]*(?=${CITATION_LINK})`,
  'gm'
);

export const normalizeResearchCitationMarkdown = (markdown: string) =>
  markdown
    .replace(TRAILING_CITATION_PUNCTUATION, '[$1$3]($2)')
    .replace(LIST_MARKER_BEFORE_CITATION, '$1 ');

const ResearchMarkdownComponent = ({
  message,
  className,
  components,
  remarkPlugins,
}: ResearchMarkdownProps) => {
  const normalizedMessage = normalizeResearchCitationMarkdown(message);
  const markdownComponents: Partial<Components> = {
    p: ({ children, className }) => (
      <div className={clsx(className, styles.paragraph)}>{children}</div>
    ),
    ...components,
  };

  return (
    <div className={clsx(styles.researchMarkdown, className)}>
      <Markdown
        message={normalizedMessage}
        components={markdownComponents}
        remarkPlugins={remarkPlugins}
      />
    </div>
  );
};

export const MemoResearchMarkdown = memo(
  ResearchMarkdownComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.message === nextProps.message &&
      prevProps.className === nextProps.className &&
      prevProps.remarkPlugins === nextProps.remarkPlugins
    );
  }
);
